import { useEffect, useState } from 'react';
import { WebSocketStatus } from '#/positions/entities/types';
import { ApiWebSocket } from '@paradigm/api/src/internal/createWebSocket';
import { useEffectOnce } from '@paradigm/utils/src/hooks';

const INITIAL_STATUS: WebSocketStatus = {
  error: '',
  connected: false,
  // instead of `connected: boolean`, should we just pass the `readyState` value? see: https://github.com/tradeparadigm/mono/blob/1c242864140ff9501c5f1e3f41808c6b71c5559e/ui/packages/api/src/internal/createWebSocket.ts#L94-L99
  // readyState: 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED'
};

/**
 *
 * @param createWebsocket concrete function to create the specialized websocket instance
 * @returns {{ ws: T, status: WebSocketStatus }}
 */
export function useWebsocket<T extends ApiWebSocket<N>, N>(
  createWebsocket: () => T,
): { ws: T; status: WebSocketStatus } {
  const [ws] = useState(createWebsocket);
  const [status, setStatus] = useState<WebSocketStatus>(INITIAL_STATUS);

  useEffect(() => {
    const handleOpen = () => {
      setStatus({
        error: '',
        connected: true,
      });
    };

    const handleClose = () => {
      setStatus({
        error: 'Disconnected from Paradigm servers. Trying to reconnect…',
        connected: false,
      });
    };

    const handleError = () => {
      setStatus({
        error: 'Error connecting to Paradigm servers',
        connected: false,
      });
    };

    ws.addEventListener('open', handleOpen);
    ws.addEventListener('close', handleClose);
    ws.addEventListener('error', handleError);

    return () => {
      ws.removeEventListener('open', handleOpen);
      ws.removeEventListener('close', handleClose);
      ws.removeEventListener('error', handleError);
    };
  }, [ws]);

  /** Closes connection on unmount */
  useEffectOnce(() => {
    return () => ws.close(1000);
  });

  return { ws, status };
}
